import Vue from 'vue';
import {
  Action,
  getModule,
  Module,
  Mutation,
  MutationAction,
  VuexModule
} from 'vuex-module-decorators';

import BasketsApi from '@/api/baskets';
import OrdersApi from '@/api/order';
import { MutationTypes, OrderMethods } from '@/enums';
import { initialUnencryptedStorage } from '@/globals';
import store from '@/store';
import { submitInitiateCheckout } from '@/utils/events.utils';
import { parseDate } from '@/utils/global.util';

import UserModule from './user';

const name = 'basket';

@Module({
  namespaced: true,
  name,
  dynamic: true, // important!
  store, // module is dynamically added to the store here (so after the store was created, instead of during the creation)
  preserveState: Boolean(initialUnencryptedStorage[name]) // preserve state when available from local storage, else don't preserve
})
class BasketModule extends VuexModule {
  // state
  orderModel = {} as OrderModel;
  data = {} as Basket;
  store: typeof store;
  upsellItems = {} as BasketUpsellGroup;
  itemsNotTransferred = null;
  placedOrderBasketId = null;
  tipManuallyUpdated = false;
  customerFirstName = null;

  // getters
  get id() {
    return this.current?.id || '';
  }

  get current() {
    return this.data;
  }

  get hasProducts() {
    return !!this.current?.products?.length;
  }

  get isDelivery() {
    return this.current?.deliveryMode === OrderMethods.DELIVERY || this.current?.deliveryMode === OrderMethods.DISPATCH;
  }

  get placedOrderGuid() {
    return this.orderModel.id;
  }

  get orderBasketId(): string {
    return this.placedOrderBasketId || '';
  }

  get restaurantId() {
    return this.current?.vendorId;
  }

  get deliveryMode() {
    return this.current?.deliveryMode;
  }

  get timeMode() {
    return this.current?.timeMode;
  }

  get appliedRewards() {
    return this.current?.appliedRewards;
  }

  get appliedDiscounts() {
    return this.current?.discounts;
  }

  get suggestedTipPercentage() {
    return this.current?.suggestedTipPercentage;
  }

  get isTipManuallyUpdated() {
    return this.tipManuallyUpdated;
  }

  get showArrivalNotification() {
    if (this.orderModel.arrivalStatus && this.orderModel.arrivalStatus !== '') {
      return true;
    }
    return false;
  }

  get curbsideDetailsExist() {
    return ['Make', 'Model', 'Color'].every((label) => this.current?.customFields?.find((field) => field.label === label && field.value));
  }

  get isAdvancedTimeValid() {
    const timeWantedString = this.current.timeWanted;
    const timeWanted = parseDate(timeWantedString);
    const today = new Date();

    if (timeWanted > today) {
      return true;
    }
    return false;
  }

  // mutations
  @Mutation
  setBasket(basket) {
    this.data = basket;
  }

  @Mutation
  setItemsNotTransferred(itemsNotTransferred) {
    this.itemsNotTransferred = itemsNotTransferred;
  }

  @Mutation
  setTipManuallyUpdated() {
    this.tipManuallyUpdated = true;
  }

  @Mutation
  setUpsellItems(upsellItems) {
    if (!Object.keys(upsellItems || {})?.length) {
      return;
    }
    this.upsellItems = upsellItems;
  }

  @Mutation
  setCustomerFirstName(firstName) {
    this.customerFirstName = firstName;
  }

  // actions
  @Action({ commit: 'setBasket', rawError: true })
  async getBasket(basketId) {
    return BasketsApi.getOne(basketId);
  }

  @Action({ rawError: true })
  async createPickupBasket({ restaurantId }) {
    try {
      const basketResponse = await BasketsApi.createForPickup(restaurantId);
      this.context.commit('setBasket', basketResponse);
      this.store.dispatch('fetchRestaurant', { restaurantId: basketResponse.vendorId })
        .then(async (restaurant) => {
          try {
            await this.store.dispatch('fetchRestaurantMenu', { slug: restaurant.slug, fetchFromState: false });
            this.store.commit(MutationTypes.SET_ACTIVE_CATEGORY, null);
          } catch (err) {
            Vue.$log.error(err);
          }
        }).catch(Vue.$log.error);
    } catch (err) {
      Vue.$log.error(err, 'error creating basket');
      throw err;
    }
  }

  @Action({ rawError: true })
  async createDeliveryBasket({ deliveryAddress }) {
    try {
      const response = await BasketsApi.createForDelivery(deliveryAddress as DeliveryAddress);
      const { basket, customerLocation } = response as any;
      this.context.commit('setBasket', basket);
      this.store.dispatch('fetchRestaurant', { restaurantId: basket.vendorId })
        .then(async (restaurant) => {
          try {
            await this.store.dispatch('fetchRestaurantMenu', { slug: restaurant.slug, fetchFromState: false });
            this.store.commit(MutationTypes.SET_ACTIVE_CATEGORY, null);
            UserModule.setLocation(customerLocation);
          } catch (err) {
            Vue.$log.error(err);
          }
        }).catch(Vue.$log.error);
    } catch (err) {
      Vue.$log.error(err, 'error creating delivery basket');
      throw err;
    }
  }

  @Action({ rawError: true })
  async updateBasketToDelivery({ basketId, deliveryAddress }: {basketId: string; deliveryAddress: any}) {
    try {
      const response = await BasketsApi.updateBasketToDelivery(basketId, deliveryAddress as DeliveryAddress);
      const { basket, itemsNotTransferred, customerLocation } = response as any;
      this.context.commit('setItemsNotTransferred', itemsNotTransferred);
      this.context.commit('setBasket', basket);
      this.store.dispatch('fetchRestaurant', { restaurantId: basket.vendorId })
        .then(async (restaurant) => {
          try {
            await this.store.dispatch('fetchRestaurantMenu', { slug: restaurant.slug });
            this.store.commit(MutationTypes.SET_ACTIVE_CATEGORY, null);
            UserModule.setLocation(customerLocation);
          } catch (err) {
            Vue.$log.error(err);
          }
        }).catch(Vue.$log.error);
    } catch (err) {
      Vue.$log.error(err, 'error updating delivery basket');
      throw err;
    }
  }

  @Action({ rawError: true })
  async createCurbsideBasket({ restaurantId, requestModel }) {
    try {
      const basketResponse = await BasketsApi.createForCurbside(restaurantId, requestModel);
      this.context.commit('setBasket', basketResponse);
      this.store.dispatch('fetchRestaurant', { restaurantId: basketResponse.vendorId })
        .then(async (restaurant) => {
          try {
            await this.store.dispatch('fetchRestaurantMenu', { slug: restaurant.slug, fetchFromState: false });
            this.store.commit(MutationTypes.SET_ACTIVE_CATEGORY, null);
          } catch (err) {
            Vue.$log.error(err);
          }
        }).catch(Vue.$log.error);

      return basketResponse;
    } catch (err) {
      Vue.$log.error(err, 'error creating curbside basket');
      throw err;
    }
  }

  @Action({ commit: 'setBasket', rawError: true })
  async addItem({ basketId, choices, productId, quantity, specialInstructions }) {
    const productIdParsed = parseInt(productId, 10);
    submitInitiateCheckout(productIdParsed, quantity);
    return BasketsApi.addProduct(basketId, {
      productId: productIdParsed,
      quantity: quantity,
      specialInstructions: specialInstructions,
      commaSeparatedChoiceIds: choices
    });
  }

  @Action({ commit: 'setBasket', rawError: true })
  async updateProduct({ id: basketProductId, choices, productId, quantity, specialInstructions }) {
    const commaSeparatedChoiceIds: string =
      Array.isArray(choices)
        ? choices.every((c) => typeof c === 'string')
          ? (choices as string[]).join(',')
          : choices.map((choice: BasketProductChoice) => choice.optionId).join(',')
        : choices as string;

    return BasketsApi.updateProduct(
      this.context.getters.id,
      basketProductId,
      {
        productId,
        quantity,
        specialInstructions,
        commaSeparatedChoiceIds
      }
    );
  }

  @Action({ commit: 'setBasket', rawError: true })
  async quickAddItem({ basketId, productId, productModifierOptionId, quantity }) {
    try {
      submitInitiateCheckout(productId, quantity);
      return await BasketsApi.quickAddProduct(basketId, {
        productId: productId,
        productModifierOptionId: productModifierOptionId,
        quantity: quantity,
        restaurantId: this.context.rootGetters.activeRestaurant.id
      });
    } catch (err) {
      Vue.$log.error('quickAddItem', err);
      throw err;
    }
  }

  @Action({ commit: 'setBasket', rawError: true })
  async removeItem({ basketId, basketProductId }) {
    try {
      return await BasketsApi.deleteProduct(basketId, basketProductId);
    } catch (err) {
      Vue.$log.error('removeItem', err);
      throw err;
    }
  }

  @Action({ rawError: true })
  async setCurbsideDetails({ make, model, color }) {
    const basketResponse = await BasketsApi.setCurbsideDetails(this.context.getters.id, {
      make: make,
      model: model,
      color: color
    });
    this.context.commit('setBasket', basketResponse);
    this.store.dispatch('fetchRestaurant', { restaurantId: basketResponse.vendorId });
  }

  @Action({ commit: 'setBasket', rawError: true })
  async setHandoffMethod(
    { method }: { method: string }
  ) {
    return BasketsApi.setHandoffMethod(this.context.getters.id, method);
  }

  @Action({ commit: 'setBasket', rawError: true })
  async setTimeWanted({ year = 0, month = 0, day = 0, hour = 0, minute = 0 }) {
    const requestModel: TimeWantedRequest = {
      day: day,
      hour: hour,
      minute: minute,
      month: month,
      year: year,
    };
    return BasketsApi.setTimeWanted(this.context.getters.id, requestModel);
  }

  @Action({ rawError: true })
  async transferBasket({ restaurantId }): Promise<Partial<BasketModule> & any> {
    const basketId = this.context.getters.id;
    const response = await BasketsApi.transferBasket(basketId, restaurantId);
    const { basket, itemsNotTransferred } = response as any;
    this.context.commit('setBasket', basket);
    this.context.commit('setItemsNotTransferred', itemsNotTransferred);
    this.store.dispatch('fetchRestaurant', { restaurantId: basket.vendorId })
      .then(async (restaurant) => {
        try {
          await this.store.dispatch('fetchRestaurantMenu', { slug: restaurant.slug, fetchFromState: false });
          this.store.commit(MutationTypes.SET_ACTIVE_CATEGORY, null);
        } catch (err) {
          Vue.$log.error(err);
        }
      }).catch(Vue.$log.error);
  }

  @Action({ commit: 'setUpsellItems' })
  async getUpsellItems({ basketId }) {
    return await BasketsApi.getUpsellItems(basketId);
  }

  @Action({ commit: 'setBasket', rawError: true })
  async addUpsellItems({ basketUpsellItems }) {
    const basketId = this.context.getters.id;
    return await BasketsApi.addUpsellItems(basketId, basketUpsellItems);
  }

  @Action({ commit: 'setBasket', rawError: true })
  async addCoupon({ basketId, couponCode }) {
    return await BasketsApi.addCoupon(basketId, couponCode);
  }

  @Action({ commit: 'setBasket', rawError: true })
  async removeCoupon({ basketId }) {
    return await BasketsApi.removeCoupon(basketId);
  }

  @Action({ commit: 'setBasket', rawError: true })
  async addReward({ basketId, membershipId, referenceId }) {
    return await BasketsApi.addReward(basketId, membershipId, referenceId);
  }

  @Action({ commit: 'setBasket', rawError: true })
  async removeReward({ basketId, rewardId }) {
    return await BasketsApi.removeReward(basketId, rewardId);
  }

  @Action({ commit: 'setBasket', rawError: true })
  async setTip({ basketId, amount }) {
    return await BasketsApi.setTip(basketId, amount);
  }

  @Action({ commit: 'setBasket', rawError: true })
  async validateBasket({ basketId }) {
    const response = await BasketsApi.validateBasket(basketId);
    const { readyTime, ...rest } = response;
    return Object.assign({}, this.context.getters.current, { ...rest, earliestReadyTime: readyTime })
  }

  @MutationAction({ mutate: ['orderModel', 'data', 'placedOrderBasketId', 'tipManuallyUpdated'] })
  async placeOrder(
    { basketId, submitRequest }: { basketId: string; submitRequest: SubmitBasketRequest }
  ): Promise<any> {
    const orderModel = await BasketsApi.placeOrder(basketId, submitRequest);
    return { orderModel: orderModel, data: {} as Basket, placedOrderBasketId: basketId, tipManuallyUpdated: false };
  }

  @MutationAction({ mutate: ['orderModel', 'data', 'placedOrderBasketId', 'tipManuallyUpdated'] })
  async placeOrderMultiPayment(
    { basketId, submitRequestMultiPayment }: { basketId: string; submitRequestMultiPayment: SubmitBasketRequestMultiPayment }
  ): Promise<any> {
    const orderModel = await BasketsApi.placeOrderMultiPayment(basketId, submitRequestMultiPayment);
    return { orderModel: orderModel, data: {} as Basket, placedOrderBasketId: basketId, tipManuallyUpdated: false };
  }

  @MutationAction({ mutate: ['orderModel'] })
  async checkOrderStatus(orderGuid: string) {
    const orderModel = await OrdersApi.checkStatus(orderGuid);
    return { orderModel: orderModel }
  }

  @MutationAction({ mutate: ['orderModel'] })
  async sendArrivalNotification({ orderGuid, message }: { orderGuid: string; message: string | null }) {
    const orderModel = await OrdersApi.sendArrivalNotification(orderGuid, message);
    return { orderModel: orderModel }
  }

  @Action({ rawError: true })
  async createBasketFromPreviousOrder({ orderRef, oloId }: { orderRef: string; oloId: string }) {
    const basketResponse = await BasketsApi.createBasketFromPreviousOrder(orderRef, oloId);
    this.context.commit('setBasket', basketResponse);
    return this.store.dispatch('fetchRestaurant', { restaurantId: basketResponse.vendorId })
      .then(async (restaurant) => {
        try {
          await this.store.dispatch('fetchRestaurantMenu', { slug: restaurant.slug, fetchFromState: false });
          this.store.commit(MutationTypes.SET_ACTIVE_CATEGORY, null);
          return restaurant;
        } catch (err) {
          Vue.$log.error(err);
        }
      }).catch(Vue.$log.error);
  }

  @Action({ rawError: true })
  async createBasketFromFavorite({ favoriteId }: { favoriteId: number }) {
    const basketResponse = await BasketsApi.createBasketFromFavoriteOrder(favoriteId);
    this.context.commit('setBasket', basketResponse);
    return this.store.dispatch('fetchRestaurant', { restaurantId: basketResponse.vendorId })
      .then(async (restaurant) => {
        try {
          await this.store.dispatch('fetchRestaurantMenu', { slug: restaurant.slug, fetchFromState: false });
          this.store.commit(MutationTypes.SET_ACTIVE_CATEGORY, null);
        } catch (err) {
          Vue.$log.error(err);
        }
      }).catch(Vue.$log.error);
  }
}
export default getModule(BasketModule);
